import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PageShell } from '../components/PageShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const NewsListPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/news`).then(r => setPosts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <PageShell>
      <section className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <p className="font-heading text-[10px] tracking-[0.4em] uppercase text-primary-main/80 mb-3">The Latest</p>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white">News &amp; Updates</h1>

        {loading ? (
          <p className="mt-12 text-white/40">Loading…</p>
        ) : posts.length === 0 ? (
          <div className="mt-12 text-center py-16 border border-white/[0.04] rounded-2xl">
            <p className="font-body text-sm text-white/40">No posts yet. Check back soon.</p>
          </div>
        ) : (
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {posts.map((post) => (
              <Link
                key={post.id}
                to={`/news/${post.slug}`}
                data-testid={`news-card-${post.slug}`}
                className="group block rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden hover:border-primary-main/30 transition-colors"
              >
                {post.image_url ? (
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={post.image_url} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                ) : (
                  <div className="aspect-[16/9] bg-gradient-to-br from-primary-main/10 to-secondary-main/10" />
                )}
                <div className="p-5">
                  <p className="font-mono text-[10px] text-white/30 uppercase tracking-wider">{new Date(post.published_at).toLocaleDateString()}</p>
                  <h3 className="mt-2 font-heading text-base font-bold text-white group-hover:text-primary-main transition-colors line-clamp-2">{post.title}</h3>
                  {post.excerpt && <p className="mt-2 font-body text-sm text-white/50 line-clamp-3">{post.excerpt}</p>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </PageShell>
  );
};

export const NewsDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios.get(`${API}/news/${slug}`)
      .then(r => setPost(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  return (
    <PageShell>
      <article className="max-w-[820px] mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <button
          onClick={() => navigate('/news')}
          className="mb-8 inline-flex items-center gap-2 text-white/50 hover:text-primary-main text-sm font-heading tracking-[0.2em] uppercase transition-colors"
          data-testid="news-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> All news
        </button>

        {notFound ? (
          <p className="text-white/40">Post not found.</p>
        ) : !post ? (
          <p className="text-white/40">Loading…</p>
        ) : (
          <>
            <p className="font-mono text-[10px] text-white/30 uppercase tracking-wider">{new Date(post.published_at).toLocaleDateString()}</p>
            <h1 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-white" data-testid="news-title">{post.title}</h1>
            {post.image_url && (
              <div className="mt-8 rounded-2xl overflow-hidden border border-white/[0.06]">
                <img src={post.image_url} alt={post.title} className="w-full block" />
              </div>
            )}
            <div
              className="mt-8 font-body text-base text-white/80 leading-relaxed whitespace-pre-wrap"
              data-testid="news-body"
            >{post.body}</div>
          </>
        )}
      </article>
    </PageShell>
  );
};
